import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wrench, Camera, X, Send, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { createMaintenance } from '../lib/api';

const MAX = 500;

// Gray wrench on a checkout card; the icon + label turn amber (open), red (urgent) or
// green (resolved) to reflect state. Click opens an inline report panel on the same card.
export default function MaintenanceControl({ checkout, enabled, onSubmitted, className = '' }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [urgent, setUrgent] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  if (!enabled) return null;

  const state = checkout.maintenance_status; // 'urgent' | 'open' | 'done' | undefined
  const stateColor = state === 'urgent'
    ? 'text-red-500 hover:text-red-600'
    : state === 'open'
      ? 'text-amber-500 hover:text-amber-600'
      : state === 'done'
        ? 'text-emerald-500 hover:text-emerald-600'
        : 'text-slate-400 hover:text-slate-600';

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) { toast.error('Please choose an image'); return; }
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  };

  const removePhoto = () => {
    setPhoto(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!comment.trim()) { toast.error('Please describe the issue'); return; }
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append('propertyId', checkout.propertyId);
      form.append('comment', comment.trim());
      if (urgent) form.append('urgent', 'true');
      if (checkout.id) form.append('scanId', checkout.id);
      if (photo) form.append('file', photo);
      await createMaintenance(form);
      toast.success('Maintenance reported');
      setComment('');
      setUrgent(false);
      removePhoto();
      setOpen(false);
      onSubmitted?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 text-xs font-semibold transition-colors ${stateColor} ${className}`}
        data-testid={`maintenance-toggle-${checkout.id}`}
        title="Report a maintenance issue"
      >
        <Wrench size={16} className={state === 'urgent' ? 'animate-pulse' : ''} />
        Maintenance
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="w-full order-last overflow-hidden"
            data-testid={`maintenance-panel-${checkout.id}`}
          >
            <div className="mt-1 bg-slate-50 rounded-xl p-3 border border-slate-100">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, MAX))}
                rows={3}
                placeholder="Describe what needs fixing (e.g. leaking tap in the bathroom)…"
                className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-400"
                style={{ fontSize: '16px' }}
                data-testid={`maintenance-comment-${checkout.id}`}
              />
              <div className="flex justify-end -mt-0.5">
                <span className={`text-[11px] font-medium ${comment.length >= MAX ? 'text-red-500' : comment.length >= MAX - 50 ? 'text-amber-500' : 'text-slate-400'}`}>
                  {comment.length}/{MAX}
                </span>
              </div>

              {preview ? (
                <div className="relative inline-block mt-1">
                  <img src={preview} alt="attachment" className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                  <button type="button" onClick={removePhoto} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-slate-200 shadow flex items-center justify-center text-slate-500" data-testid={`maintenance-remove-photo-${checkout.id}`}>
                    <X size={13} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700"
                  data-testid={`maintenance-add-photo-${checkout.id}`}
                >
                  <Camera size={15} /> Add photo (optional)
                </button>
              )}
              <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pickPhoto} data-testid={`maintenance-file-${checkout.id}`} />

              <button
                type="button"
                onClick={() => setUrgent((v) => !v)}
                className={`mt-3 w-full h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 border transition-colors ${urgent ? 'bg-red-500 border-red-500 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-red-300 hover:text-red-500'}`}
                data-testid={`maintenance-urgent-${checkout.id}`}
              >
                <AlertTriangle size={15} />
                {urgent ? 'Marked as urgent' : 'Mark as urgent'}
              </button>

              <div className="flex gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => { setOpen(false); setComment(''); setUrgent(false); removePhoto(); }}
                  className="flex-1 h-10 rounded-lg text-sm font-semibold border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submit}
                  disabled={submitting || !comment.trim()}
                  className="flex-1 h-10 rounded-lg text-sm font-semibold text-white flex items-center justify-center gap-1.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 disabled:opacity-50"
                  data-testid={`maintenance-submit-${checkout.id}`}
                >
                  {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
                  Report
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
